import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Breadcrumbs,
  Link,
  Paper,
  Typography,
  Chip,
  IconButton,
  CircularProgress
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { usePermissions } from '../../../GlobalFunction/userPermissions';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectProperties,
  selectGlobalProperty,
  selectLocalProperty,
  setLocalProperty,
  fetchProperties
} from '../../../redux/propertySlice';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import HomeIcon from '@mui/icons-material/Home';
import moment from 'moment';
import axiosInstance from '../../../lib/axios';
import { toast } from 'react-toastify';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import 'ag-grid-community/styles/ag-theme-alpine.css';
import { transform } from 'lodash';

const gridStyles = `
  .ag-theme-alpine {
    --ag-border-color: #c8c8c8;
    --ag-header-background-color: #f8f9fa;
    --ag-odd-row-background-color: #fcfcfc;
    --ag-border-radius: 6px;
  }
  
  .ag-theme-alpine .ag-cell {
    border-right: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    align-items: center;
  }
  
  .ag-theme-alpine .ag-header-cell {
    border-right: 1px solid #c8c8c8;
    border-bottom: 1px solid #c8c8c8;
    font-weight: 600;
  }
  
  .ag-theme-alpine .ag-floating-filter-input {
    border-radius: 4px;
  }
  
  .ag-theme-alpine .ag-floating-filter-input:focus {
    outline: none;
    border-color: #3f51b5;
    box-shadow: 0 0 0 2px rgba(63, 81, 181, 0.2);
  }
  
  .ag-theme-alpine .ag-header-cell::after {
    display: none;
  }
  
  .ag-theme-alpine .ag-ltr .ag-cell-first-right-pinned {
    border-left: 1px solid #c8c8c8;
  }
  
  .ag-floating-filter-button{
    margin-left: 0px !important 
  }
  
  .ag-header-cell{
    padding-left: 2px;
    padding-right: 0px;
  }
  
  .truncated-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
  
  .status-chip {
    height: 20px;
    font-size: 0.75rem;
    font-weight: 600;
  }
  
   .ag-input-wrapper:before {
    background-color: white;
    display:none
  }
    .ag-theme-alpine .ag-floating-filter input[type="date"]{
      padding-left: 0 !important;
    }
      :where(.ag-ltr) .ag-header-cell-resize {
    right: -6px !important;
}
`;

const TruncatedCellRenderer = (props) => {
  const [isOverflowed, setIsOverflowed] = useState(false);
  const cellRef = React.useRef(null);

  useEffect(() => {
    if (cellRef.current) {
      const isTextOverflowed =
        cellRef.current.scrollWidth > cellRef.current.clientWidth ||
        (props.value && props.value.length > 20);
      setIsOverflowed(isTextOverflowed);
    }
  }, [props.value]);

  const cellValue = props.valueFormatted ? props.valueFormatted : props.value;

  return (
    <Tooltip
      title={isOverflowed ? cellValue : ''}
      placement="top"
      arrow
      disableHoverListener={!isOverflowed}
    >
      <div
        ref={cellRef}
        className="truncated-cell"
        style={{ width: '100%' }}
      >
        {cellValue}
      </div>
    </Tooltip>
  );
};

const StatusCellRenderer = (props) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'Generated':
        return 'green';
      case 'Pending':
        return 'red';
      default:
        return '#757575';
    }
  };

  return (
    <Chip
      label={props.value}
      className="status-chip"
      sx={{
        backgroundColor: getStatusColor(props.value),
        color: 'white',
        width: '100%',
        maxWidth: '100px',
      }}
    />
  );
};

const columnDefs = [
  { headerName: 'Booking No', field: 'BookNo', width: 100, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Book Date', field: 'BookDt', width: 110, filter: 'agDateColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Resident', field: 'ResidentName', width: 170, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Mobile', field: 'MobileNo', width: 110, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Room No', field: 'RoombedName', width: 90, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Room Type', field: 'RoomTypeName', width: 120, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'BookType', field: 'BookingType', width: 80, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
  {
    headerName: 'BookSt',
    field: 'BookSt',
    width: 130,
    filter: 'agTextColumnFilter',
    cellRenderer: (params) => {
      const status = params.value === "C" ? "Confirmed" : params.value === "B" ? "Booked" : params.value;
      return <TruncatedCellRenderer {...params} value={status} />;
    }
  },
  {
    headerName: 'A/C St',
    field: 'IsAccGen',
    width: 100,
    filter: 'agTextColumnFilter',
    cellRenderer: StatusCellRenderer,
    cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' }
  },
  { headerName: 'Plan Name', field: 'PlanName', width: 210, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Duration', field: 'Duration', width: 70, filter: 'agNumberColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Deposit', field: 'DepositeAmt', width: 100, filter: 'agNumberColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Dep Paid', field: 'IsDepPaid', width: 60, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'TotalRent', field: 'TotalRent', width: 120, filter: 'agNumberColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'TopUpDays', field: 'Topupdays', width: 100, filter: 'agNumberColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'TopUp Amt', field: 'TopupAmt', width: 100, filter: 'agNumberColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Discount', field: 'PromoDiscAmt', width: 90, filter: 'agNumberColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Addone1', field: 'TrspChrg', width: 100, filter: 'agNumberColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Addone2', field: 'MessChrg', width: 100, filter: 'agNumberColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Net Payable', field: 'AdvcRcble', width: 100, filter: 'agNumberColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'CheckIn', field: 'CheckinDt', width: 100, filter: 'agDateColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'CheckOut', field: 'CheckoutDt', width: 100, filter: 'agDateColumnFilter', cellRenderer: TruncatedCellRenderer },
  { headerName: 'Email', field: 'Email', width: 240, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
];

const BookingConfirmTable = () => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const permissions = usePermissions("Booking Confirmation");
  const { AddRec = "0" } = permissions || {};
  const dispatch = useDispatch();
  const properties = useSelector(selectProperties);
  const globalProperty = useSelector(selectGlobalProperty);
  const localProperty = useSelector(state => selectLocalProperty(state, 'bookingConfirm'));
  const selectedProperty = localProperty || globalProperty;
  const [gridApi, setGridApi] = useState(null);
  const UserId = sessionStorage.getItem('UserId');

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = gridStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    if (properties.length === 0) {
      dispatch(fetchProperties());
    }
    if (selectedProperty) {
      fetchBookingConfirm();
    }
  }, [selectedProperty]);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const handlePropertyChange = (event) => {
    const selectedId = event.target.value;
    dispatch(setLocalProperty({
      componentId: 'bookingConfirm',
      propertyId: selectedId
    }));
  };

  const fetchBookingConfirm = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post(`TrnBooking/GetTrnBookingDashBoard`,
        {
          PageNumber: 1,
          PageSize: 0,
          SearchText: '',
          Flag:"Cf",
          MobileNo :'',
          BookNo:'',
          PropId:selectedProperty,
        },
      );

      if (response.data.Status === 0) {
        const formattedData = response.data.Data.map(bconfirm => ({
          ...bconfirm,
          BookDt: bconfirm.BookDt ? moment(bconfirm.BookDt).format('DD-MM-YYYY') : '',
          CheckinDt: bconfirm.CheckinDt ? moment(bconfirm.CheckinDt).format('DD-MM-YYYY') : '',
          CheckoutDt: bconfirm.CheckoutDt ? moment(bconfirm.CheckoutDt).format('DD-MM-YYYY') : '',
          BookSt: bconfirm.BookSt === "C" ? "Confirmed" : bconfirm.BookSt === "B" ? "Booked" : "",
          IsAccGen: bconfirm.IsAccGen === "G" ? "Generated" : "Pending",
        }));
        setRows(formattedData);

        if (gridApi) {
          setTimeout(() => {
            gridApi?.sizeColumnsToFit();
          }, 0);
        }
      } else {
        toast.info(response.data.message);
        setRows([]);
      }
    } catch (error) {
      console.error("Error fetching booking confirm data:", error);
      toast.error('Error fetching booking confirm data');
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  const ClearFilter = useCallback(() => {
    if (gridApi) {
      gridApi.setFilterModel(null);
    }
  }, [gridApi]);



  const handleHomeClick = () => {
    navigate('/dashboard');
  };

  const handleRowDoubleClick = useCallback((params) => {
    const bookId = params.data.BookId;
    const url = `/precheckin?BookId=${encodeURIComponent(bookId)}&mode=view`;
    window.open(url, '_blank');
  }, []);

  const handleLocationclick = () => {
    navigate('/enquiry/pre-registration')
  }

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
    filterParams: {
      suppressAndOrCondition: true,
    },
    cellStyle: {
      borderRight: '1px solid #e0e0e0',
      display: 'flex',
      alignItems: 'center'
    },
    headerClass: 'ag-header-cell',
    suppressSizeToFit: true,
  }), []);

  return (
    <Box sx={{ width: '100%', height: '90vh', display: 'flex', flexDirection: 'column', p: 2, background: 'rgb(249, 249, 249)' }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        background: 'rgb(255, 255, 255)',
        padding: '12px',
        borderRadius: '5px',
        boxShadow: 'rgb(0 0 0 / 8%)',
        flexWrap: 'nowrap',
        overflow: 'hidden'
      }}>
        <Breadcrumbs
          aria-label="breadcrumb"
          separator="›"
          sx={{
            fontSize: { xs: "14px", sm: "16px" },
            display: "flex",
            flexDirection: { xs: "column", sm: "column", md: "row" }
          }}
        >
          <Link
            underline="hover"
            color="#635bff"
            sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
            onClick={handleHomeClick}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            <Box component="span">Home</Box>
          </Link>

          <Link
            underline="hover"
            color="#635bff"
            sx={{ cursor: "pointer", fontWeight: "500" }}
            onClick={handleLocationclick}
          >
            Booking(Pre-Registration)
          </Link>

          <Typography color="text.primary" sx={{ fontWeight: "500" }}>Booking Confirmation</Typography>
        </Breadcrumbs>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexShrink: 0,
          ml: 2
        }}>
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel id="property-select-label">Filter by Property</InputLabel>
            <Select
              labelId="property-select-label"
              id="property-select"
              value={selectedProperty || ''}
              onChange={handlePropertyChange}
              label="Filter by Property"
            >
              {properties.map((property) => (
                <MenuItem key={property.Id} value={property.Id}>
                  {property.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title="Clear all filters" arrow>
            <span>
              <Button
                variant="outlined"
                onClick={ClearFilter}
                size="small"
                // startIcon={<ClearIcon />}
                sx={{
                  borderRadius: "50px",
                  textTransform: "none",
                  borderColor: "#635bff",
                  color: "#635bff",
                  "&:hover": {
                    borderColor: "#524ccc",
                    backgroundColor: "rgba(99, 91, 255, 0.04)",
                  }
                }}
              >
                Clear Filter
              </Button>
            </span>
          </Tooltip>
          <Tooltip title='Previous Page' arrow>
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1,
              justifyContent: "flex-start"
            }}
            >
              <IconButton
                onClick={() => navigate('/enquiry/pre-registration')}
                sx={{
                  backgroundColor: '#635bff',
                  borderRadius: '50%',
                  width: 35,
                  height: 35,
                  '&:hover': {
                    backgroundColor: '#635bff',
                  },
                  boxShadow: 3,
                }}
              >
                <ArrowBackIcon sx={{ color: 'white' }} />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      <Paper
        elevation={2}
        sx={{
          height: 'calc(100vh - 250px)',
          width: '100%',
          flexGrow: 1,
          overflow: 'hidden'
        }}
      >
        <Box
          className="ag-theme-alpine"
          style={{
            height: '100%',
            width: '100%',
            overflowX: 'auto'
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'transform(-50%, -50%)',
                zIndex: 10,
                textAlign: 'center',
              }}
            >
              <CircularProgress size='3rem' sx={{ color: '#635bff' }} />
              <Typography sx={{ marginTop: 2, color: '#635bff', fontWeight: "bold" }}>
                Loading Data...
              </Typography>
            </Box>
          ) : (
            <AgGridReact
              columnDefs={columnDefs}
              rowData={rows}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              onRowDoubleClicked={handleRowDoubleClick}
              pagination={true}
              paginationPageSize={100}
              paginationPageSizeSelector={[50, 100, 500, 1000, 5000]}
              suppressRowClickSelection={true}
              rowSelection={'single'}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              loading={isLoading}
              rowHeight={30}
              headerHeight={30}
              floatingFiltersHeight={28}
              overlayLoadingTemplate={
                '<span class="ag-overlay-loading-center">Loading data...</span>'
              }
              overlayNoRowsTemplate={
                '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;">No data found</span>'
              }
              suppressMenuHide={true}
              enableRangeSelection={true}
              sideBar={false}
              suppressHorizontalScroll={false}
              getRowStyle={(params) => ({
                backgroundColor: params.node.rowIndex % 2 === 0 ? '#f1f5fd' : '#ffffff'
              })}
            />
          )}
        </Box>
      </Paper>
      <Typography variant="body2" sx={{ fontWeight: 'bold', position: 'absolute', bottom: '32px', }}>
        Total Records: {rows.length}
      </Typography>
    </Box>
  );
}

export default BookingConfirmTable;